const GRAPH = 'https://graph.microsoft.com/v1.0';

async function graphFetch(token, path, options = {}) {
  const res = await fetch(`${GRAPH}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text();
    console.error(`OneNote API ${res.status} ${res.statusText} — ${path}`, body);
    throw new Error(`Graph ${res.status} ${res.statusText}: ${body}`);
  }
  return res;
}

export async function listNotebooks(token) {
  const res = await graphFetch(token, '/me/onenote/notebooks');
  return res.json();
}

export async function listSections(token, notebookId) {
  const res = await graphFetch(token, `/me/onenote/notebooks/${notebookId}/sections`);
  return res.json();
}

export async function listPages(token, sectionId, top = 20) {
  const res = await graphFetch(token, `/me/onenote/sections/${sectionId}/pages?$top=${top}`);
  return res.json();
}

// Fetches every page in a section via $skip pagination (API max $top is 100).
export async function listAllPages(token, sectionId) {
  const all = [];
  let skip = 0;
  const top = 100;
  while (true) {
    const res  = await graphFetch(token, `/me/onenote/sections/${sectionId}/pages?$top=${top}&$skip=${skip}`);
    const data = await res.json();
    const batch = data.value || [];
    all.push(...batch);
    if (batch.length < top) break;
    skip += top;
  }
  return all;
}

export async function getPageMetadata(token, pageId) {
  const res = await graphFetch(token, `/me/onenote/pages/${pageId}`);
  return res.json();
}

export async function createPage(token, sectionId, htmlBody) {
  const res = await graphFetch(token, `/me/onenote/sections/${sectionId}/pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/xhtml+xml' },
    body: htmlBody,
  });
  return res.json();
}

export async function deletePage(token, pageId) {
  const res = await graphFetch(token, `/me/onenote/pages/${pageId}`, {
    method: 'DELETE',
  });
  return res.status;
}

export async function searchPages(token, query, top = 20) {
  const url = `/me/onenote/pages?$search=${encodeURIComponent(query)}&$top=${top}`;
  const res = await graphFetch(token, url);
  return res.json();
}

// Searches OneNote content via Microsoft Search API (POST /search/query).
// /me/onenote/pages?$search is unsupported by Graph (error 20108).
// Returns normalized objects filtered to OneNote hits only.
export async function searchPagesWithContext(token, query, size = 100) {
  const res = await graphFetch(token, '/search/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      requests: [{
        entityTypes: ['driveItem'],
        query: { queryString: query },
        from: 0,
        size,
        fields: ['name', 'webUrl', 'createdDateTime', 'lastModifiedDateTime', 'parentReference'],
      }],
    }),
  });
  const data = await res.json();
  const hits = data.value?.[0]?.hitsContainers?.[0]?.hits || [];
  console.log('[searchPagesWithContext] raw hits:', hits.length);
  hits.forEach((hit, i) => {
    const r = hit.resource || {};
    console.log(`  [${i}] name="${r.name}" path="${r.parentReference?.path}" webUrl="${r.webUrl}"`);
  });

  const mapped = hits.map(hit => {
    const r         = hit.resource || {};
    const name      = r.name      || '';
    const webUrl    = r.webUrl    || '';
    const parentRef = r.parentReference || {};
    const hasFileExt = /\.\w{2,4}$/.test(name);
    return {
      pageId:          hit.hitId,
      title:           name,
      summary:         hit.summary || '',
      webUrl,
      parentReference: parentRef,
      isOneNote:
        name.endsWith('.one') ||
        !hasFileExt ||
        (parentRef.path || '').includes('.one') ||
        (parentRef.path || '').includes('/Notebooks/') ||
        webUrl.includes('.one') ||
        webUrl.includes('/Notebooks/') ||
        webUrl.includes('wd=target'),
    };
  });

  const filtered = mapped.filter(r => r.isOneNote);
  console.log('[searchPagesWithContext] after filter:', filtered.length);
  return filtered;
}

export async function createNotebook(token, displayName) {
  const res = await graphFetch(token, '/me/onenote/notebooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  return res.json();
}

export async function createSection(token, notebookId, displayName) {
  const res = await graphFetch(token, `/me/onenote/notebooks/${notebookId}/sections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  return res.json();
}

// Platform-wide search across all SharePoint/OneDrive content.
// Returns all hits with no entity-type filtering — resultType is determined
// by webUrl path signals (see SearchDestination.js for the consumer).
export async function searchEverything(token, query, size = 50) {
  const res = await graphFetch(token, '/search/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      requests: [{
        entityTypes: ['driveItem', 'listItem'],
        query: { queryString: query },
        from: 0,
        size,
        fields: ['name', 'webUrl', 'createdDateTime', 'lastModifiedDateTime', 'parentReference', 'size'],
      }],
    }),
  });
  const data = await res.json();
  const hits = data.value?.[0]?.hitsContainers?.[0]?.hits || [];

  return hits.map(hit => {
    const r         = hit.resource || {};
    const name      = r.name      || '';
    const webUrl    = r.webUrl    || '';
    const parentRef = r.parentReference || {};
    const path      = parentRef.path || '';

    let resultType;
    if (
      name.endsWith('.one') ||
      !/\.\w{2,4}$/.test(name) ||
      path.includes('.one') ||
      path.includes('/Notebooks/') ||
      webUrl.includes('.one') ||
      webUrl.includes('/Notebooks/') ||
      webUrl.includes('wd=target')
    ) {
      resultType = 'note';
    } else if (webUrl.includes('/FamilyVault_Photos/')) {
      resultType = 'photo';
    } else if (webUrl.includes('/FIGVault/')) {
      resultType = 'fig';
    } else if (webUrl.includes('/Family%20Vault/')) {
      resultType = 'document';
    } else {
      resultType = 'other';
    }

    return {
      id:           hit.hitId,
      name,
      webUrl,
      summary:      hit.summary || '',
      lastModified: r.lastModifiedDateTime || '',
      parentPath:   path,
      resultType,
    };
  });
}
